import Cookies from 'js-cookie';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { scrollTo } from '@/components/hooks/hash';
import { useIsRouterChanging } from '@/components/hooks/useIsRouterChanging';
import { Button } from '@/components/shared/element/buttons/button';
import { Text } from '@/components/shared/element/text';
import { Label } from '@/components/shared/form/label';
import { Radio } from '@/components/shared/form/radio';
import { Select } from '@/components/shared/form/select';
import { TextField } from '@/components/shared/form/text-field';
import { Box } from '@/components/shared/layout/box';
import { Grid } from '@/components/shared/layout/grid';
import { removeTags } from '@/components/shared/utility/format';
import { THANK } from '@/const/cookie';
import { ERROR_MSG } from '@/const/text';
import { DEALER_DATA } from '@/contents/lcv/dealers';
import clientLead from '@/services/lead/client';
import logger from '@/services/logging';
import { styled } from '@/stitches.config';
import { trackFormInitiated, trackFormSuccess } from '@/tag-manager';
import { IDealer } from '@/types/dealer/dealer';
import { IVehicleType } from '@/types/services/contentstack';
import { IBody as ILeadBody, IModel } from '@/types/services/lead';

declare const window: any;

const InputWrap = styled('div', {
  display: 'grid',
  rowGap: '$2',
});

export interface ILeadForm {
  title: string;
  campaignName: string;
  campaignId: string;
  campaignVersion: string;
  successURL?: string;
  lcvModel?: IModel;
  fbModel?: string;
  vehicleType?: IVehicleType;
  showVehicleType?: false | boolean;
  isDialogForm?: false | boolean;
  interestType?: '' | string;
  remark?: '' | string;
  onOpenThankyouDialog?: (value: boolean) => void;
  abTesting?: {
    experiment: string;
    variant: string;
  };
}

interface IInput {
  value: string;
  pattern: RegExp;
  require: boolean;
  error: true | false | '';
}

interface IForm {
  fullname: IInput;
  mobile: IInput;
}

const NAME_PATTERN = new RegExp(String.raw`^[\p{L}\p{M}\p{Zs}-]{2,50}$`, 'u');
const MOBILE_PATTERN = /^0\d{8,10}$/;

const View = (props: ILeadForm) => {
  const formRef = useRef<HTMLFormElement | null>(null);

  const [form, setForm] = useState<IForm>({
    fullname: {
      value: '',
      pattern: NAME_PATTERN,
      require: true,
      error: '',
    },
    mobile: {
      value: '',
      pattern: MOBILE_PATTERN,
      require: true,
      error: '',
    },
  });

  const [startValidate, setStartValidate] = useState(false);
  const [invalidSubmit, setInvalidSubmit] = useState(false);
  const [dealerId, setDealerId] = useState('');
  const [loading, setLoading] = useState(false);
  const [lcvModel, setLCVModel] = useState<IModel>(props.lcvModel);

  const isRouterChanging = useIsRouterChanging();
  const router = useRouter();

  const [dealers, setDealers] = useState<IDealer[]>([]);
  const campaignName = removeTags(props.campaignName);

  const onChangeText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const _field: IInput = { ...form[e.target.name], value: e.target.value };
    const _form = { ...form };
    _form[e.target.name] = _field;

    if (startValidate) {
      validate(_form);
    }
    setForm(_form);
  };

  useEffect(() => {
    setDealers(DEALER_DATA);
  }, []);

  const validate = (_form: IForm) => {
    let isValid = true;

    Object.keys(_form).forEach((key) => {
      const input: IInput = _form[key];
      input.error = false;

      if (input.require) {
        if (input.value === '') {
          input.error = true;
          isValid = false;
          return;
        }

        if (!input.pattern.test(input.value)) {
          input.error = true;
          isValid = false;
          return;
        }
      } else {
        if (input.value !== '') {
          if (!input.pattern.test(input.value)) {
            input.error = true;
            isValid = false;
            return;
          }
        }
      }
    });

    // dealerId
    if (!dealerId) isValid = false;

    return isValid;
  };

  useEffect(() => {
    if (!invalidSubmit) return;
    setInvalidSubmit(false);

    const $form = formRef.current;
    if (!$form) return;
    const $error = $form.querySelector('.is-error') as HTMLElement | null;
    if ($error) scrollTo($error, -48);
  }, [invalidSubmit]);

  useEffect(() => {
    trackFormInitiated({
      formId: props.campaignId,
      formName: campaignName,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _onSubmit = async () => {
    if (loading) return;

    setStartValidate(true);
    const isValid = validate(form);

    if (!isValid) {
      setInvalidSubmit(true);
      return;
    }

    const parts = form.fullname.value.trim().split(/\s+/);
    const firstName = (parts.shift() ?? '').trim();
    const surname = parts.join(' ').trim();

    setLoading(true);
    try {
      const dealer = dealers.find((o) => o.dealer_id === dealerId);

      const body: ILeadBody = {
        campaignName,
        campaignId: '999',
        ctsCampaignName: campaignName,
        ctsCampaignId: props.campaignId,
        ctsCampaignVersion: props.campaignVersion,
        firstName,
        surname,
        mobile: form.mobile.value,
        dealerId: dealer?.dealer_id ?? '',
        InterestModel: lcvModel ?? '',
        branchCode: dealer?.uid ?? '',
        branchEmail: dealer?.sale.email ?? '',
        branchName: dealer?.mirai.name_km ?? '',
        model: lcvModel,
        remark: props.remark,
        utm_source: window._utm_source, // from tagmanager
        utm_medium: window._utm_medium, // from tagmanager
        gclId: window._gclid, // from tagmanager
        fbclid: window._fbclid, // from tagmanager
        ttclid: window._ttclid, // from tagmanager
      };

      await clientLead({
        data: body,
      });

      trackFormSuccess({
        formId: props.campaignId,
        formName: campaignName,
      });

      Cookies.set(THANK, true, { expires: 10 / 86400 }); // 10 secs

      if (props.isDialogForm) {
        props.onOpenThankyouDialog?.(true);
      } else {
        if (!isRouterChanging && props.successURL) {
          router.push(props.successURL);
        }
      }
    } catch (e) {
      logger.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeModel = (name: string, value: IModel) => {
    setLCVModel(value);
  };

  useEffect(() => {
    if (!router.isReady) return;
    const m = router.query?.model as string | undefined;
    if (m === 'ppv' || m === 'pickup') setLCVModel(m as IModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady]);

  const dealerError = startValidate && dealerId === '' && dealers.length > 0;

  return (
    <form ref={formRef}>
      <Grid rowGap="4">
        {!!props.title && (
          <Box>
            <Text as="h3" size="h6" font="bold">
              {props.title}
            </Text>
          </Box>
        )}
        {!!props.showVehicleType && (
          <InputWrap>
            <Label
              for="radio-model"
              required
              test={{
                dataTest: 'label_selectModelType',
              }}
            >
              ជ្រើសរើសម៉ូដែលរថយន្ត
            </Label>
            <Radio
              size={3}
              id="radio-model"
              name="model"
              value={lcvModel}
              items={[
                {
                  value: 'pickup',
                  label: 'រថយន្តភីកអាប់',
                },
                {
                  value: 'ppv',
                  label: 'រថយន្តលក្ខណៈគ្រួសារ',
                },
              ]}
              onUpdate={handleChangeModel}
              removeAnimation
            />
          </InputWrap>
        )}
        <InputWrap>
          <Label for="input-fullname" required>
            ឈ្មោះនាមត្រកូល
          </Label>
          <TextField
            id="input-fullname"
            disabled={loading}
            type="text"
            placeholder="ឈ្មោះ - នាមត្រកូល"
            name="fullname"
            className={form.fullname.error ? `is-error` : ``}
            value={form.fullname.value}
            onChange={onChangeText}
          />
          {!!form.fullname.error && (
            <Text as="p" id="fullname-error" size="xs" variant="text-red">
              {ERROR_MSG}
            </Text>
          )}
        </InputWrap>

        <InputWrap className="InputWrap">
          <Label for="input-mobile" required>
            លេខទូរសព្ទ
          </Label>
          <TextField
            id="input-mobile"
            maxLength={11}
            disabled={loading}
            type="tel"
            placeholder="បំពេញលេខទូរស័ព្ទរបស់អ្នក"
            name="mobile"
            inputMode="numeric"
            value={form.mobile.value}
            onChange={onChangeText}
            onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (!/[0-9]/.test(event.key)) {
                if (event.cancelable) event.preventDefault();
              }
            }}
            className={form.mobile.error ? `is-error` : ``}
          />
          {!!form.mobile.error && (
            <Text as="p" id="mobile-error" size="xs" variant="text-red">
              {ERROR_MSG}
            </Text>
          )}
        </InputWrap>

        <Box>
          <Text as="h3" size="h6" font="bold">
            ជ្រើសរើសសាខា
          </Text>
        </Box>

        <InputWrap>
          <Label for="select-dealer" required>
            សាខា
          </Label>

          <Select
            id="select-dealer"
            name="dealer"
            required
            disabled={loading}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setDealerId(e.target.value)
            }
            value={dealerId}
            className={dealerError ? 'is-error' : ''}
            error={dealerError}
          >
            <option value="">ជ្រើសរើសសាខា</option>
            {dealers.map((o) => (
              <option key={o.dealer_id} value={o.dealer_id}>
                {o.mirai.name_km}
              </option>
            ))}
          </Select>
          {!!dealerError && (
            <Text as="p" id="dealer-error" size="xs" variant="text-red">
              {ERROR_MSG}
            </Text>
          )}
        </InputWrap>

        <Box
          css={{
            '@maxlg': { textAlign: 'center' },
            '@lg': { textAlign: `${props.isDialogForm ? 'center' : 'start'}` },
          }}
        >
          <Button
            name="submit"
            label="បញ្ជាក់"
            isLoading={loading}
            isMinWidth
            onClick={_onSubmit}
            css={{ '@maxlg': { width: '100%' } }}
            tracking={{
              dataTrack: 'lead-form',
              dataTrackText: 'ยืนยัน',
              dataTrackUrl: props.successURL,
            }}
            test={{
              dataTest: 'button_submit',
            }}
          />
        </Box>
      </Grid>
    </form>
  );
};

View.displayName = 'SharedFormLeadForm';
export default View;
